import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    toggleEditModal,
    setEditedUser,
    toggleNeedRefresh,
} from '../../../store/usersSlice';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg';
import { ReactComponent as EditIcon } from '../../icons/edit.svg';
import { ReactComponent as TrashIcon } from '../../icons/trash.svg';
import { ReactComponent as LockIcon } from '../../icons/lock.svg';
import { ReactComponent as UnlockIcon } from '../../icons/unlock.svg';
import '../organizer_edit_panel/OrganizerEditPanel.css';
import './UserEditPanel.css';

function UserEditPanel({ allUsers = [] }) {
    const dispatch = useDispatch();
    const editModalOpen = useSelector((state) => state.users.editModalOpen);
    const editedUser = useSelector((state) => state.users.editedUser);

    const [activeTab, setActiveTab] = useState('basic');
    const [formData, setFormData] = useState({
        nickname: '',
        firstName: '',
        lastName: '',
        gender: '',
        phone: '',
        email: '',
        description: '',
    });

    const nameParts = useMemo(() => {
        const parts = (editedUser?.fullName || '').split(' ');
        return {
            firstName: parts[0] || '',
            lastName: parts.slice(1).join(' ') || '',
        };
    }, [editedUser?.fullName]);

    useEffect(() => {
        if (editedUser) {
            setFormData({
                nickname: editedUser.nickname || '',
                firstName: nameParts.firstName,
                lastName: nameParts.lastName,
                gender: editedUser.gender || '',
                phone: editedUser.phone || '',
                email: editedUser.email || '',
                description: editedUser.description || '',
            });
        }
    }, [editedUser, nameParts.firstName, nameParts.lastName]);

    const currentIndex = allUsers.findIndex((user) => user.id === editedUser?.id);
    const totalCount = allUsers.length;

    const handlePrevious = useCallback(() => {
        if (currentIndex > 0) {
            dispatch(setEditedUser(allUsers[currentIndex - 1]));
        }
    }, [currentIndex, allUsers, dispatch]);

    const handleNext = useCallback(() => {
        if (currentIndex < allUsers.length - 1) {
            dispatch(setEditedUser(allUsers[currentIndex + 1]));
        }
    }, [currentIndex, allUsers, dispatch]);

    const handleClose = useCallback(() => {
        dispatch(toggleEditModal());
        dispatch(setEditedUser(null));
    }, [dispatch]);

    const handleInputChange = useCallback((field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const handleSave = useCallback(() => {
        console.log('Save user data:', formData);
        dispatch(toggleNeedRefresh());
        handleClose();
    }, [formData, dispatch, handleClose]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('pl-PL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).replace(/\//g, '-');
    };

    if (!editModalOpen || !editedUser) {
        return null;
    }

    return (
        <>
            <div className="organizer-edit-backdrop" onClick={handleClose} />
            <div className="organizer-edit-panel">
                <div className="organizer-edit-header">
                    <div className="header-left">
                        <div className="navigation-arrows">
                            <button
                                className="nav-arrow-btn"
                                onClick={handlePrevious}
                                disabled={currentIndex <= 0}
                                title="Poprzedni"
                                type="button"
                            >
                                <ArrowLeftIcon className="nav-arrow-icon" />
                            </button>
                            <button
                                className="nav-arrow-btn"
                                onClick={handleNext}
                                disabled={currentIndex >= totalCount - 1}
                                title="Następny"
                                type="button"
                            >
                                <ArrowRightIcon className="nav-arrow-icon" />
                            </button>
                        </div>
                        <span className="results-counter">
                            {currentIndex + 1} z {totalCount} wyników
                        </span>
                    </div>
                    <div className="header-right">
                        <span className="registration-date">
                            Data rejestracji: {formatDate(editedUser.registrationDate)}
                        </span>
                    </div>
                </div>

                <div className="organizer-edit-content">
                    <div className="company-header-section">
                        <div className="company-info">
                            <div className="company-id-name">
                                <span className="company-id">(ID {editedUser.id})</span>
                                <h2 className="company-name">{editedUser.nickname}</h2>
                            </div>
                        </div>
                    </div>

                    <div className="detail-tabs-nav">
                        <div className="tabs-container">
                            <button
                                className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
                                onClick={() => setActiveTab('basic')}
                                type="button"
                            >
                                Dane podstawowe
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'description' ? 'active' : ''}`}
                                onClick={() => setActiveTab('description')}
                                type="button"
                            >
                                Opis
                            </button>
                        </div>
                    </div>

                    {activeTab === 'basic' && (
                        <div className="tab-content">
                            <div className="data-section">
                                <div className="data-row two-column">
                                    <div className="data-row-inner">
                                        <div className="data-label">ID</div>
                                        <div className="data-value">{editedUser.id}</div>
                                    </div>
                                    <div className="data-row-inner">
                                        <div className="data-label">Nick</div>
                                        <div className="data-value-input">
                                            <input
                                                type="text"
                                                className="edit-input"
                                                value={formData.nickname}
                                                onChange={(e) => handleInputChange('nickname', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="data-row two-column">
                                    <div className="data-row-inner">
                                        <div className="data-label">Imię</div>
                                        <div className="data-value-input">
                                            <input
                                                type="text"
                                                className="edit-input"
                                                value={formData.firstName}
                                                onChange={(e) => handleInputChange('firstName', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="data-row-inner">
                                        <div className="data-label">Nazwisko</div>
                                        <div className="data-value-input">
                                            <input
                                                type="text"
                                                className="edit-input"
                                                value={formData.lastName}
                                                onChange={(e) => handleInputChange('lastName', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="data-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">Płeć</div>
                                        <div className="data-value-input">
                                            <input
                                                type="text"
                                                className="edit-input"
                                                value={formData.gender}
                                                onChange={(e) => handleInputChange('gender', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="data-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">Numer telefonu</div>
                                        <div className="data-value-input">
                                            <input
                                                type="text"
                                                className="edit-input"
                                                value={formData.phone}
                                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="data-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">Adres e-mail</div>
                                        <div className="data-value-input">
                                            <input
                                                type="email"
                                                className="edit-input"
                                                value={formData.email}
                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'description' && (
                        <div className="tab-content description-data">
                            <div className="data-section">
                                <div className="data-row description-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">Opis profilu</div>
                                        <div className="data-value-input">
                                            <textarea
                                                className="edit-textarea"
                                                rows={6}
                                                value={formData.description}
                                                onChange={(e) => handleInputChange('description', e.target.value)}
                                                placeholder="Opis profilu..."
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="organizer-edit-footer">
                    <div className="footer-buttons">
                        <button className="action-btn outline danger disabled" disabled type="button">
                            <TrashIcon className="btn-icon" />
                            <span>Usuń</span>
                        </button>
                        <button className="action-btn outline disabled" disabled type="button">
                            {editedUser.status === 'blocked' ? (
                                <>
                                    <UnlockIcon className="btn-icon" />
                                    <span>Odblokuj</span>
                                </>
                            ) : (
                                <>
                                    <LockIcon className="btn-icon" />
                                    <span>Zablokuj</span>
                                </>
                            )}
                        </button>
                        <button className="action-btn filled disabled" disabled type="button">
                            <EditIcon className="btn-icon" />
                            <span>Edytuj</span>
                        </button>

                        <div className="edit-actions-wrapper">
                            <button className="action-btn outline morski" onClick={handleClose} type="button">
                                <span>Odrzuć zmiany</span>
                            </button>
                            <button className="action-btn filled" onClick={handleSave} type="button">
                                <span>Zapisz zmiany</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default UserEditPanel;
